import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import Chip, { type ChipProps } from '@mui/material/Chip';
import Radio from '@mui/material/Radio';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';

import { CardGallery } from '../CardGallery/CardGallery';
import { CardIconList } from '../CardIconList/CardIconList';
import type { PhotoEvidenceItem } from '../PhotoEvidenceList/PhotoEvidenceList';
import { Typography } from '../Typography/Typography';

interface CardContentProps {
  images?: PhotoEvidenceItem[];
  title: React.ReactNode;
  action?: React.ReactNode;
  subtitle?: React.ReactNode;
  chip?: ChipProps & {
    isLoading?: boolean;
  };
  children?: React.ReactNode;
}

interface CardProps extends CardContentProps {
  isSelected?: boolean;
  clickable?: boolean;
  muted?: boolean;
  selectable?: boolean;
  selectionVariant?: 'radio' | 'checkbox';
  onClick?: () => void;
  'data-testid'?: string;
}

const CardContent = ({ images, title, action, subtitle, chip, children }: CardContentProps) => {
  const cardTitle = title ? (
    <Typography variant="body1" component="span" data-testid="card-title" fontWeight="bold">
      {title}
    </Typography>
  ) : null;

  return (
    <div>
      {images ? (
        <Box
          sx={{
            mb: 2,
          }}
        >
          <CardGallery images={images} />
        </Box>
      ) : null}
      {action ? (
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {cardTitle}
          <div>{action}</div>
        </Stack>
      ) : (
        cardTitle
      )}
      {subtitle || chip ? (
        <Box
          sx={{
            display: 'flex',
            width: '100%',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {subtitle ? (
            <Typography variant="body2" color="secondary" data-testid="card-subtitle">
              {subtitle}
            </Typography>
          ) : null}

          {chip?.isLoading ? <Skeleton variant="rounded" width={30} /> : null}
          {chip?.label ? <Chip size="small" {...chip} /> : null}
        </Box>
      ) : null}
      {children ? (
        <Box
          sx={{
            mt: title ? 1 : 0,
          }}
        >
          {children}
        </Box>
      ) : null}
    </div>
  );
};

export const Card = ({
  images,
  title,
  action,
  subtitle,
  chip,
  isSelected = false,
  children,
  clickable = false,
  muted = false,
  selectable = false,
  selectionVariant = 'radio',
  onClick,
  'data-testid': dataTestId,
  ...rest
}: CardProps) => {
  const isInteractive = clickable || selectable;

  return (
    <Box
      onClick={onClick}
      data-testid={dataTestId}
      sx={(theme) => ({
        ...(isInteractive && {
          ':hover': {
            cursor: 'pointer',
          },
          boxShadow: theme.shadows[2],
        }),
        ...(muted && {
          opacity: 0.4,
          pointerEvents: 'none',
        }),

        backgroundColor: isSelected ? theme.palette.primary[200] : theme.palette.background.paper,
        border: isSelected ? `1px solid ${theme.palette.primary[400]}` : `1px solid ${theme.palette.border.main}`,
        borderRadius: 2,
        padding: 2,
        transition: theme.transitions.create(['box-shadow', 'background-color', 'border-color']),
      })}
      {...rest}
    >
      {selectable ? (
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          {selectionVariant === 'checkbox' ? <Checkbox checked={isSelected} /> : <Radio checked={isSelected} />}
          <CardContent images={images} title={title} action={action} subtitle={subtitle} chip={chip}>
            {children}
          </CardContent>
        </Stack>
      ) : (
        <CardContent images={images} title={title} action={action} subtitle={subtitle} chip={chip}>
          {children}
        </CardContent>
      )}
    </Box>
  );
};

Card.displayName = 'Card';
Card.UnorderedList = CardIconList;
